import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Mic, ShieldCheck, Users, BarChart3, Headphones, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth, login } from "../context/AuthContext";
import { SiteLayout } from "../components/site/SiteLayout";
import { LifeThemes, useLifePublic } from "../components/site/LifeShowcase";
import { SpeakTopics, useSpeakPublic } from "../components/site/SpeakShowcase";

const MAP = "https://static.prod-images.emergentagent.com/jobs/bbbc05ed-34dc-430c-a8e1-f70aee71a63a/images/84d50cd937906b5e4466d76f8dfa8b359d078b4af06d78e5fdced0d127c61aa5.jpeg";

const LIFE_COVER = "https://static.prod-images.emergentagent.com/jobs/bbbc05ed-34dc-430c-a8e1-f70aee71a63a/images/07d6aa34fec2894616af7c5c2faf8131a4c1234833374563d41b075b0c00cbdb.jpeg";

const SPEAK_COVER = "https://static.prod-images.emergentagent.com/jobs/bbbc05ed-34dc-430c-a8e1-f70aee71a63a/images/2f4ca46dcaa088fad91c9ae90e1dec05439b021d4ef249524a4446621ddecfad.jpeg";

const ART_COVER = "https://static.prod-images.emergentagent.com/jobs/bbbc05ed-34dc-430c-a8e1-f70aee71a63a/images/2e2356f61cb6d2283a1cf416e4ac64f593c370856b23481fdf84eba546fedbf1.jpeg";
const ZHUYIN_COVER = "https://static.prod-images.emergentagent.com/jobs/bbbc05ed-34dc-430c-a8e1-f70aee71a63a/images/732806532ebc1d67bcd20b09009fe666d38a379a3c86d18bc486dd2218e0b66b.jpeg";

const HALLS = [
  {
    key: "classic", name: "經典故事館", img: MAP, accent: "var(--vermilion)", soft: "bg-[#FFF4E0]", border: "border-[#e2c48f]", font: "font-serif-tc",
    tagline: "跟著古人冒險了解中華典故", fit: "想打好注音和識字基礎的孩子",
    learn: ["37 個注音符號與四聲", "常用繁體字與筆順", "經典故事裡的品格與智慧"],
    flow: ["看故事", "學注音", "認國字", "描一描", "玩遊戲", "理解題", "得獎勵"],
    content: "孔融讓梨、曹沖稱象等 20 個經典故事，一站一站解鎖",
  },
  {
    key: "life", name: "生活中文館", img: LIFE_COVER, accent: "#2c6e65", soft: "bg-[#E9F6F1]", border: "border-[#9fd3c7]", font: "font-round",
    tagline: "從每天的生活學說話", fit: "想多聽多說、把中文用在生活裡的孩子",
    learn: ["生活詞語（每篇 6 個）", "實用句型，例如「我想要＿＿。」", "跟著說，練習開口"],
    flow: ["看故事", "聽故事", "學新詞", "學句型", "聽音選圖", "圖文配對", "跟我說", "理解題", "得獎勵"],
    content: "我的家、吃飯、上學、去超市等 8 個主題，24 個原創小故事",
  },
  {
    key: "speak", name: "我會說中文館", img: SPEAK_COVER, accent: "#8F3520", soft: "bg-[#FFF1E6]", border: "border-[#F5C9AE]", font: "font-round",
    tagline: "勇敢開口說中文", fit: "在海外長大、聽得懂卻不太敢開口的孩子",
    learn: ["12 個生活情境，每課 10 句實用中文", "繁體中文＋注音，台灣華語標準發音", "錄下自己的聲音，和標準音比一比"],
    flow: ["看情境", "聽句子", "看字和注音", "選回應", "跟著說", "錄音比一比", "小對話", "得獎勵"],
    content: "「我要……」「我不舒服」「跟阿公阿嬤說」等 12 課，120 句生活中文，一課完成再開下一課",
  },
  {
    key: "zhuyin", name: "注音練習館", img: ZHUYIN_COVER, accent: "#1D6FB8", soft: "bg-[#E3F2FB]", border: "border-[#b9dcf0]", font: "font-serif-tc",
    tagline: "37 個注音打穩基礎", fit: "剛開始學注音，或想把ㄅㄆㄇ練熟的孩子",
    learn: ["37 張注音卡：聯想圖、口訣、教育部標準發音", "看筆順動畫，跟著描一描", "聽音選注音，隨時看自己的注音進度"],
    flow: ["看卡片", "聽發音", "念口訣", "看筆順", "描一描", "連續練習", "聽音選注音", "看進度"],
    content: "聲母 21 個、介音 3 個、韻母 13 個，免費方案也全部開放",
  },
  {
    key: "art", name: "小書童畫坊", img: ART_COVER, accent: "#B4471F", soft: "bg-[#FFF0F3]", border: "border-[#f7c6d2]", font: "font-round",
    tagline: "畫畫培養創意美感和了解中華文化圖騰", fit: "喜歡塗塗畫畫、想練習手眼協調的孩子",
    learn: ["塗色、跟著畫，蠟筆水彩筆貼紙都有", "燈籠、十二生肖、中國龍，邊畫邊聽典故", "作品放進作品集，掛上自己的小畫廊"],
    flow: ["選活動", "聽說明", "塗一塗", "跟著畫", "聽典故", "取名字", "選畫框", "掛上畫廊"],
    content: "10 個塗色、10 個跟著畫、6 個文化美術、5 個故事角色創作，還有自由畫板",
  },
];

const ART_PREVIEW = [["k01", "紅燈籠"], ["k03", "中國龍"], ["k04", "月亮和玉兔"], ["s01", "曹沖的大象"], ["s02", "孔融的一籃梨"], ["c01", "愛吃竹子的熊貓"]];

const ArtPreview = () => (
  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="home-art-preview">
    {ART_PREVIEW.map(([k, t]) => (
      <div key={k} className="rounded-2xl bg-white border-2 border-[#f7c6d2] p-2 text-center">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/art/line/${k}.png`} alt={t} className="aspect-square w-full object-contain" />
        <div className="text-xs sm:text-sm font-bold text-stone-600 mt-1 truncate">{t}</div>
      </div>
    ))}
  </div>
);

const ZY_GROUPS = [["聲母", "ㄅㄆㄇㄈㄉㄊㄋㄌㄍㄎㄏㄐㄑㄒㄓㄔㄕㄖㄗㄘㄙ", "#1D6FB8"], ["介音", "ㄧㄨㄩ", "#D9573B"], ["韻母", "ㄚㄛㄜㄝㄞㄟㄠㄡㄢㄣㄤㄥㄦ", "#3E9C8F"]];

const ZhuyinPreview = () => (
  <div className="space-y-4" data-testid="home-zhuyin-preview">
    {ZY_GROUPS.map(([name, syms, color]) => (
      <div key={name} className="flex flex-wrap items-center gap-2">
        <span className="px-3 py-1 rounded-full text-white text-sm font-bold mr-1" style={{ background: color }}>{name}</span>
        {[...syms].map((s) => <span key={s} className="w-11 h-11 rounded-xl bg-white border-2 flex items-center justify-center text-2xl font-black font-serif-tc" style={{ borderColor: `${color}55`, color }}>{s}</span>)}
      </div>
    ))}
  </div>
);

const SHARED = [
  [Users, "同一個孩子檔案", "五館共用暱稱和頭像，切換學習館不必重新設定。"],
  [Trophy, "星星徽章一起累積", "在哪一館學習（包括畫坊完成的作品），星星、徽章和貼紙都收進同一本獎勵簿。"],
  [BarChart3, "一份學習報告", "總覽各館的完成度和學習時間，也能分館查看注音、國字、生活詞語和開口說的次數。"],
  [Mic, "家人的聲音", "在家長錄音室錄下故事、詞語和句子，各館都會改用家人的聲音。"],
  [Headphones, "只聽故事", "經典和生活兩館都能一鍵連續播放，適合睡前或坐車時聽。"],
  [ShieldCheck, "安心的環境", "沒有廣告、沒有聊天、不公開孩子資料，帳號由家長管理。"],
];

const PATHS = [
  ["3–4 歲", "從生活中文館開始", "先用熟悉的生活情境聽、說詞語，建立對中文的親切感。"],
  ["5–7 歲", "以經典故事館為主", "有系統地學完注音和繁體字，再到生活館練習句子。"],
  ["海外長大", "每天到我會說中文館說幾句", "一課約 10 分鐘，聽、選、說、錄音，把中文帶進每天的生活。"],
  ["每週安排", "五館交替", "例如每天先到注音練習館描幾個注音，再輪流聽經典故事、生活故事，週末到小書童畫坊畫一幅畫，睡前說幾句中文。"],
];

const Hero = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-12 pb-20 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center" data-testid="home-hero">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-[var(--tea)] text-sm font-bold mb-6">注音・繁體中文・台灣華語</div>
        <h1 className="font-brush text-6xl sm:text-7xl lg:text-8xl text-[var(--vermilion)] leading-none">小書童<br />故事館</h1>
        <p className="font-serif-tc text-lg sm:text-xl text-stone-700 mt-6 max-w-xl leading-relaxed">
          多個學習館，開啟多元中文學習旅程。從 37 個注音練習、經典故事與生活中文，到口語表達與中華文化創意美術，讓孩子在故事、聲音、互動與創作中，自然學會繁體中文，也更親近中華文化、建立長久的文化連結。
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          {user ? (
            <button data-testid="hero-enter-btn" onClick={() => nav("/profiles")} className="kid-btn bg-[var(--jade)] text-white px-8 text-xl font-serif-tc">進入故事館</button>
          ) : (
            <button data-testid="landing-login-btn" onClick={login} className="kid-btn bg-[var(--vermilion)] text-white px-8 text-xl font-serif-tc flex items-center gap-3">
              <img alt="" src="https://www.google.com/favicon.ico" className="w-5 h-5 bg-white rounded-full p-0.5" /> 家長登入，開始冒險
            </button>
          )}
          <Link to="/learn" data-testid="hero-learn-link" className="kid-btn bg-white border-2 border-[var(--line)] px-8 text-xl font-serif-tc flex items-center text-[var(--tea)]">認識學習方法</Link>
          <Link to="/pricing" data-testid="hero-pricing-link" className="kid-btn bg-amber-100 border-2 border-amber-300 px-8 text-xl font-serif-tc flex items-center text-[var(--tea)]">方案與價格</Link>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="grid grid-cols-2 gap-4 sm:gap-5" data-testid="hero-halls-visual">
        {HALLS.map((h, i) => (
          <motion.a key={h.key} href={`#hall-${h.key}`} data-testid={`hero-hall-${h.key}`} whileHover={{ y: -8, rotate: 0 }}
            className={`rounded-[1.75rem] overflow-hidden border-4 bg-white shadow-xl ${h.border} ${i === 4 ? "col-span-2" : ""} ${["rotate-[-2deg]", "rotate-[2deg] translate-y-4", "rotate-[1.5deg]", "rotate-[-1.5deg] translate-y-4", "rotate-[-0.5deg] mt-2"][i]}`}>
            <img src={h.img} alt={h.name} className={`w-full object-cover ${i === 4 ? "h-24 sm:h-28" : "h-24 sm:h-32"}`} />
            <div className={`px-4 py-3 ${h.soft} ${h.font}`}>
              <div className="text-xl sm:text-2xl font-black whitespace-nowrap" style={{ color: h.accent }}>{h.name}</div>
              <div className="text-stone-500 text-sm">{h.tagline}</div>
            </div>
          </motion.a>
        ))}
      </motion.div>
    </section>
  );
};

const ClassicPreview = () => {
  const [stories, setStories] = useState([]);
  useEffect(() => { api.get("/public/stories").then(({ data }) => setStories(data)).catch(() => {}); }, []);
  return (
    <div className="flex gap-4 overflow-x-auto pb-3 snap-x" data-testid="home-classic-strip">
      {stories.map((s) => (
        <div key={s.story_id} className="scroll-card snap-start shrink-0 w-56 overflow-hidden">
          <img src={s.thumbnail} alt={s.story_title} className="h-32 w-full object-cover" />
          <div className="p-3">
            <div className="text-sm text-stone-500 font-serif-tc">第{s.story_number}課・{s.map_location}</div>
            <div className="font-serif-tc text-xl font-black mt-1">{s.story_title}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const HallInfo = ({ h }) => (
  <div className={`flex flex-col gap-5 ${h.font}`}>
    <div>
      <h3 className="text-4xl font-black" style={{ color: h.accent }}>{h.name}</h3>
      <p className="text-xl text-stone-700 mt-2">{h.tagline}</p>
      <p className="text-lg text-stone-600 mt-1">{h.content}</p>
    </div>
    <div>
      <div className="text-sm font-bold text-stone-500 mb-1">適合</div>
      <p className="text-lg text-stone-800">{h.fit}</p>
    </div>
    <div>
      <div className="text-sm font-bold text-stone-500 mb-1">學什麼</div>
      <ul className="space-y-1">{h.learn.map((l) => <li key={l} className="flex items-center gap-2 text-lg text-stone-800"><Check size={18} style={{ color: h.accent }} />{l}</li>)}</ul>
    </div>
    <div>
      <div className="text-sm font-bold text-stone-500 mb-2">每一課</div>
      <div className="flex flex-wrap gap-1.5">
        {h.flow.map((s, i) => <span key={s} className="px-3 py-1 rounded-full bg-white text-sm border" style={{ color: h.accent, borderColor: "currentColor" }}>{i + 1}. {s}</span>)}
      </div>
    </div>
  </div>
);

const HallBlock = ({ h, idx, label = "館裡的故事", children }) => (
  <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.5 }}
    id={`hall-${h.key}`} className={`scroll-mt-24 rounded-[2rem] border-2 p-6 sm:p-10 ${h.soft} ${h.border}`} data-testid={`home-hall-${h.key}`}>
    <div className={`inline-flex items-center gap-2 px-4 py-1 rounded-full bg-white border text-sm font-bold mb-6 ${h.font}`} style={{ color: h.accent, borderColor: h.accent }}>第 {idx + 1} 館</div>
    <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${idx % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <img src={h.img} alt={h.name} className="w-full h-64 sm:h-80 object-cover rounded-[1.5rem] shadow-xl" />
      <HallInfo h={h} />
    </div>
    <div className="mt-8">
      <div className={`text-lg font-black mb-4 ${h.font}`} style={{ color: h.accent }}>{label}</div>
      {children}
    </div>
  </motion.section>
);

const HallsSection = () => {
  const life = useLifePublic();
  const speak = useSpeakPublic();
  return (
    <section id="halls" className="max-w-7xl mx-auto px-6 sm:px-10 py-16 scroll-mt-20" data-testid="home-halls">
      <h2 className="font-serif-tc text-3xl sm:text-4xl font-black text-[var(--tea)]">五座學習館</h2>
      <p className="font-serif-tc text-lg text-stone-600 mt-3 mb-10 max-w-3xl">五館的課程、進度和畫面各自獨立，孩子可以依照年齡和興趣自由選擇；學到的星星、徽章和學習紀錄，則全部收在一起。</p>
      <div className="flex flex-col gap-10">
        <HallBlock h={HALLS[0]} idx={0}><ClassicPreview /></HallBlock>
        <HallBlock h={HALLS[1]} idx={1}><LifeThemes themes={life?.themes} showStories /></HallBlock>
        <HallBlock h={HALLS[2]} idx={2} label="館裡的課程"><SpeakTopics topics={speak?.topics} /></HallBlock>
        <HallBlock h={HALLS[3]} idx={3} label="館裡的 37 個注音"><ZhuyinPreview /></HallBlock>
        <HallBlock h={HALLS[4]} idx={4} label="畫坊裡的活動"><ArtPreview /></HallBlock>
      </div>
      <div className="mt-10 scroll-card p-6 sm:p-8" data-testid="home-shared">
        <h3 className="font-serif-tc text-2xl font-black text-[var(--tea)] mb-5">五館共用</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SHARED.map(([Icon, t, d]) => (
            <div key={t} className="flex gap-3">
              <span className="w-11 h-11 shrink-0 rounded-xl bg-amber-100 text-[var(--tea)] flex items-center justify-center"><Icon size={20} /></span>
              <div className="font-serif-tc"><div className="font-black text-lg">{t}</div><p className="text-stone-600">{d}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PathsSection = () => (
  <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16" data-testid="home-paths">
    <h2 className="font-serif-tc text-3xl sm:text-4xl font-black text-[var(--tea)] mb-8">怎麼搭配著學？</h2>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
      {PATHS.map(([age, t, d], i) => (
        <motion.div key={age} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="scroll-card p-6 font-serif-tc">
          <div className="seal inline-block px-3 py-1 font-black">{age}</div>
          <div className="text-2xl font-black mt-4">{t}</div>
          <p className="text-stone-600 mt-2 leading-relaxed">{d}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

export default function Landing() {
  return (
    <SiteLayout>
      <div data-testid="landing-page">
        <Hero />
        <HallsSection />
        <PathsSection />
      </div>
    </SiteLayout>
  );
}
